import { useRef, useState } from 'react';
import { api, errMsg } from '../lib/api';
import { Button, Sheet } from './ui';
import { useToast } from './feedback';

type PdfStatus = { status: 'ready'; url: string } | { status: 'pending' };

/**
 * Pede o PDF (gerado pelo Browser Rendering na Queue "reports") e faz polling até ficar pronto.
 * Se demorar, oferece a versão de impressão do navegador como alternativa.
 */
export function PdfButton({ endpoint, body, printUrl, label = 'PDF', size = 'xs', block }: {
  endpoint: string; body?: object; printUrl: string; label?: string; size?: 'xs' | 'sm' | 'lg' | null; block?: boolean;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [fallback, setFallback] = useState(false);
  const cancel = useRef(false);

  const download = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const run = async () => {
    setBusy(true);
    cancel.current = false;
    try {
      const deadline = Date.now() + 45_000;
      let first = true;
      while (!cancel.current && Date.now() < deadline) {
        const r = await api.post<PdfStatus>(endpoint, body ?? {});
        if (r.status === 'ready') {
          download(r.url);
          if (!first) toast('PDF pronto');
          return;
        }
        if (first) toast('Gerando PDF…');
        first = false;
        await new Promise((res) => setTimeout(res, 2500));
      }
      if (!cancel.current) setFallback(true);
    } catch (e) {
      toast(errMsg(e), 'err');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="dark" size={size ?? undefined} block={block} loading={busy} onClick={run}>{busy ? 'Gerando…' : label}</Button>
      <Sheet open={fallback} onClose={() => setFallback(false)} title="O PDF está demorando">
        <div className="sub" style={{ marginBottom: 16 }}>
          A geração continua em segundo plano. Você pode tentar de novo em instantes, ou abrir a versão para imprimir e salvar como PDF pelo navegador.
        </div>
        <div className="row gap-8">
          <Button className="flex1" onClick={() => { setFallback(false); void run(); }}>Tentar de novo</Button>
          <a className="btn dark flex1" href={printUrl} target="_blank" rel="noreferrer" onClick={() => setFallback(false)}>Imprimir</a>
        </div>
      </Sheet>
    </>
  );
}
